/**
 * Article + ArticleAllergen + Supplier + SupplierLot + Reception schemas.
 */

import { z } from 'zod';
import {
  ARTICLE_TYPES,
  ALLERGEN_CODES,
  FOOD_SAFETY_MODES,
  ALLERGEN_PRESENCES,
  SUPPLIER_LINK_ROLES,
  SUPPLIER_LOT_STATUSES,
  RECEPTION_STATUSES,
  RECEPTION_CONTROL_RESULTS,
  PURCHASE_ORDER_CHANNELS,
  DECLARABLE_PRICE_CHANGE_SOURCES,
  APP_LANGUAGES,
  SUPPLIER_HOMOLOGATION_STATUSES,
  SUPPLIER_DOCUMENT_TYPES,
} from '../enums/index.js';
import { documentDatesError } from '../domain/supplierHomologation.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  dateString,
  nonNegativeQuantity,
  money,
  percent,
  jsonObject,
  softDeleteFields,
  timestampFields,
  syncFields,
} from './common.js';

// ── Article ────────────────────────────────────────────────────────────────

/**
 * The manual per-100 g nutrition table (EU 1169/2011 mandatory declaration).
 * Lives on the ARTICLE (articles.nutrition_json) and is snapshot into the FT
 * on generation. Every value is optional — the obrador fills what it has.
 */
export const NutritionFacts = z
  .object({
    energy_kj: z.number().finite().nonnegative().optional(),
    energy_kcal: z.number().finite().nonnegative().optional(),
    fat_g: z.number().finite().nonnegative().optional(),
    saturated_fat_g: z.number().finite().nonnegative().optional(),
    carbohydrate_g: z.number().finite().nonnegative().optional(),
    sugars_g: z.number().finite().nonnegative().optional(),
    protein_g: z.number().finite().nonnegative().optional(),
    salt_g: z.number().finite().nonnegative().optional(),
  })
  .strict();
export type NutritionFacts = z.infer<typeof NutritionFacts>;

/**
 * Per-article characteristics of its sales container (articles.container_json,
 * paired with container_type_id → the container_types master). Distinct from
 * the packaging/logistic units: the envàs faces the FT/label; a packaging unit
 * carries a logistic conversion factor.
 */
/**
 * One free-text block of the article's "Seguretat alimentària" section
 * (articles.food_safety_json). The section is no longer a fixed set of parameters:
 * the obrador composes it as an ordered list of blocks, each an optional heading
 * plus a body, and adds as many as it needs. Snapshot into the FT on generation
 * (tech_sheets.food_safety_json) so an edit never rewrites an issued sheet.
 */
export const FreeTextBlock = z
  .object({
    title: nonEmptyString.max(200).optional(),
    body: nonEmptyString.max(4000),
  })
  .strict();
export type FreeTextBlock = z.infer<typeof FreeTextBlock>;

/** The article's food-safety blocks, in render order. */
export const FoodSafetyNotes = z.array(FreeTextBlock).max(40);
export type FoodSafetyNotes = z.infer<typeof FoodSafetyNotes>;

export const ContainerCharacteristics = z
  .object({
    units_per_container: z.number().int().positive().optional(),
    size: nonEmptyString.optional(),
    weight_g: z.number().finite().nonnegative().optional(),
    notes: nonEmptyString.optional(),
  })
  .strict();
export type ContainerCharacteristics = z.infer<typeof ContainerCharacteristics>;

export const Article = z
  .object({
    id: uuid,
    tenant_id: uuid,
    sku: nonEmptyString,
    name: nonEmptyString,
    description: z.string().trim().min(1).nullable(),
    article_type: z.enum(ARTICLE_TYPES),
    category_id: uuid.nullable(),
    is_purchased: z.boolean(),
    is_manufactured: z.boolean(),
    is_sold: z.boolean(),
    base_unit_id: uuid,
    /**
     * UNITAT DE RECEPTA (OBR-102) — només una MATÈRIA PRIMERA. La unitat en què es formula
     * la recepta quan no és la base (ous: base Un, recepta kg); NULL = la recepta va en la
     * base. `recipe_unit_factor` són les unitats de recepta que equivalen a UNA unitat base
     * (1 Un = 0,060 kg → 0,06); sempre van juntes (CHECK a la base de dades). Vegeu
     * domain/recipeUnit.ts.
     */
    recipe_unit_id: uuid.nullable(),
    recipe_unit_factor: z.number().finite().positive().nullable(),
    /**
     * CONVERSIÓ VARIABLE («pes variable» fins al 2026-09-20): l'article no té
     * equivalència fixa entre la unitat que es compta i la que es mesura, i es
     * pesa a cada moviment. La columna conserva el nom antic; el concepte i el
     * que es llegeix a la pantalla són «conversió variable».
     *
     * Amagada a l'MVP (VARIABLE_CONVERSION_ENABLED, constants/): el valor desat
     * es conserva i s'accepta, però cap pantalla el pregunta mentre recepció,
     * expedició i existències no en sàpiguen fer res.
     */
    is_variable_weight: z.boolean(),
    is_stock_tracked: z.boolean(),
    shelf_life_days: z.number().int().nonnegative().nullable(),
    /** L'impost que s'aplica a l'article (taxes master). */
    tax_id: uuid.nullable(),
    /**
     * DERIVAT de `tax_id` — un mirall que manté la base de dades (trigger
     * articles_sync_vat_rate). Es llegeix, mai s'escriu: el tipus viu al
     * mestre d'impostos, que és qui es pot canviar en un lloc i val per a
     * totes les fitxes que l'usen.
     */
    vat_rate: percent.nullable(),
    barcode: z.string().trim().min(1).nullable(),
    min_stock: nonNegativeQuantity.nullable(),
    max_stock: nonNegativeQuantity.nullable(),
    default_supplier_id: uuid.nullable(),
    /**
     * Lloc per defecte (Zona / Secció / Ubicació): where stock lands on reception
     * or production when nobody says otherwise. The server keeps the three
     * coherent — the finest level wins (a location implies its section and zone).
     */
    default_zone_id: uuid.nullable(),
    default_section_id: uuid.nullable(),
    default_location_id: uuid.nullable(),
    active_recipe_id: uuid.nullable(),
    purchase_price: money.nullable(),
    /** Preu de venda vigent PER UNITAT BASE (doc Vendes, Correccions): default
     *  for the sales-order line price (× the packaging radial factor). */
    sale_price: money.nullable(),
    cost_alert_threshold_pct: percent.nullable(),
    image_url: z.string().trim().min(1).nullable(),
    image_key: z.string().trim().min(1).nullable(),
    nutrition_json: jsonObject,
    conservation_condition_id: uuid.nullable(),
    usage_type_id: uuid.nullable(),
    preparation_mode_id: uuid.nullable(),
    container_type_id: uuid.nullable(),
    container_json: jsonObject,
    default_label_template_id: uuid.nullable(),
    food_safety_mode: z.enum(FOOD_SAFETY_MODES),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Article = z.infer<typeof Article>;

/**
 * Create payload. `sku` is OPTIONAL: when the tenant's sku_numbering_mode is
 * sequential/per_type an omitted sku is assigned server-side (assign_article_sku
 * trigger); a supplied sku always wins (imports / ERP). In free mode the server
 * rejects an omitted sku — the form enforces it client-side too.
 */
export const ArticleCreateInput = z
  .object({
    sku: nonEmptyString.optional(),
    name: nonEmptyString,
    description: nonEmptyString.optional(),
    article_type: z.enum(ARTICLE_TYPES),
    category_id: uuid.optional(),
    is_purchased: z.boolean().default(false),
    is_manufactured: z.boolean().default(false),
    is_sold: z.boolean().default(false),
    base_unit_id: uuid,
    /**
     * Unitat de recepta i la seva equivalència — només per a una matèria primera i sempre
     * juntes (vegeu {@link Article}). La coherència entre elles i amb la unitat base la
     * valida `recipeUnitIssue` (domain/recipeUnit.ts); aquí només la forma.
     */
    recipe_unit_id: uuid.optional(),
    recipe_unit_factor: z.number().finite().positive().optional(),
    /** Conversió variable — veure {@link Article}. Amagada a l'MVP. */
    is_variable_weight: z.boolean().default(false),
    is_stock_tracked: z.boolean().default(true),
    shelf_life_days: z.number().int().nonnegative().optional(),
    /**
     * L'impost de la fitxa, tria del mestre (Configuració · Impostos). NO hi ha
     * `vat_rate`: el tipus el posa el mestre i la base de dades el reflecteix a
     * la columna antiga, així ningú pot desar-ne un a mà que digui una altra cosa.
     */
    tax_id: uuid.optional(),
    barcode: z.string().trim().min(1).optional(),
    min_stock: nonNegativeQuantity.optional(),
    max_stock: nonNegativeQuantity.optional(),
    default_supplier_id: uuid.optional(),
    /** Lloc per defecte — see {@link Article}. */
    default_zone_id: uuid.optional(),
    default_section_id: uuid.optional(),
    default_location_id: uuid.optional(),
    purchase_price: money.optional(),
    sale_price: money.optional(),
    image_url: nonEmptyString.optional(),
    image_key: nonEmptyString.optional(),
    nutrition_json: NutritionFacts.optional(),
    conservation_condition_id: uuid.optional(),
    usage_type_id: uuid.optional(),
    preparation_mode_id: uuid.optional(),
    container_type_id: uuid.optional(),
    container_json: ContainerCharacteristics.optional(),
    default_label_template_id: uuid.optional(),
    /**
     * How the article's Fitxa Tècnica food-safety data is produced. Only a
     * MANUFACTURED article may choose 'derived' (from its recipe BOM); the
     * server pins a purchased-only article to 'manual' (CHECK constraint).
     */
    food_safety_mode: z.enum(FOOD_SAFETY_MODES).default('manual'),
  })
  .strict();
export type ArticleCreateInput = z.infer<typeof ArticleCreateInput>;

// ── ArticleAllergen ──────────────────────────────────────────────────────────

export const ArticleAllergen = z
  .object({
    id: uuid,
    tenant_id: uuid,
    article_id: uuid,
    allergen: z.enum(ALLERGEN_CODES),
    presence: z.enum(ALLERGEN_PRESENCES),
    is_derived: z.boolean(),
    created_at: timestamptz,
  })
  .strict();
export type ArticleAllergen = z.infer<typeof ArticleAllergen>;

/**
 * Manual allergen mark. Always valid for a PURCHASED-ONLY article; valid for a
 * MANUFACTURED one only while its `food_safety_mode` is 'manual' (see
 * domain/allergens.ts foodSafetyIsDerived). Under 'derived' the 14 EU allergens
 * come from the BOM (is_derived=true) and are never entered by hand. This client
 * input forces is_derived=false and is rejected server-side for an article in
 * derived mode (enforce_derived_allergens trigger).
 */
export const ArticleAllergenInput = z
  .object({
    article_id: uuid,
    allergen: z.enum(ALLERGEN_CODES),
    presence: z.enum(ALLERGEN_PRESENCES).default('contains'),
  })
  .strict();
export type ArticleAllergenInput = z.infer<typeof ArticleAllergenInput>;

// ── UnitConversion ─────────────────────────────────────────────────────────

/**
 * Per-article unit conversion (unit_conversions row): `to = from × factor`. A
 * per-article conversion (e.g. ud → kg for a variable-weight article) overrides
 * the tenant-wide factor. The factor MUST be strictly positive (a zero/negative
 * factor is nonsensical) and the two units MUST differ. `article_id` is the
 * scope; `tenant_id` is NEVER accepted from the client (RLS injects it).
 *
 * Writing unit_conversions is admin-only at the RLS layer; this input validates
 * the shape before the insert so a malformed factor never reaches the server.
 */
export const ConversionInput = z
  .object({
    article_id: uuid,
    from_unit_id: uuid,
    to_unit_id: uuid,
    factor: z.number().finite().positive(),
  })
  .strict()
  .refine((c) => c.from_unit_id !== c.to_unit_id, {
    message: 'from_unit_id and to_unit_id must differ',
    path: ['to_unit_id'],
  });
export type ConversionInput = z.infer<typeof ConversionInput>;

// ── ArticlePackagingUnit ─────────────────────────────────────────────────────

/**
 * Per-article packaging (logistic) unit with a RADIAL factor to the article's
 * base unit: 1 <packaging unit> = quantity_in_base <base units> ("1 Sac =
 * 25 kg"). Factors never chain packaging→packaging (doc decision). At most one
 * row per article is the default PURCHASE unit (reception preselect) and at
 * most one the default SALES unit (sales-order line preselect — doc Vendes,
 * Correccions); no default row means the base unit is the respective default.
 * tenant_id is NEVER accepted from the client (RLS + DB DEFAULT inject it).
 */
export const ArticlePackagingUnitInput = z
  .object({
    article_id: uuid,
    packaging_unit_id: uuid,
    quantity_in_base: z.number().finite().positive(),
    is_default_purchase: z.boolean().default(false),
    is_default_sales: z.boolean().default(false),
  })
  .strict();
export type ArticlePackagingUnitInput = z.infer<typeof ArticlePackagingUnitInput>;

// ── WorkOrderPackagingLabel ──────────────────────────────────────────────────

/**
 * De quins envasos va demanar etiqueta l'operari en tancar una ordre d'envasat.
 *
 * L'envàs i el factor hi van CONGELATS, no per clau forana a la configuració de
 * l'article: si demà algú la canvia, el que es va demanar aquell dia ha de
 * seguir dient el que deia. És el mateix criteri pel qual una OT es queda la
 * versió de recepta que tenia al davant. La identitat d'una fila és l'envàs: una
 * ordre no demana dues vegades la mateixa caixa.
 */
export const WorkOrderPackagingLabelInput = z
  .object({
    work_order_id: uuid,
    packaging_unit_id: uuid,
    quantity_in_base: z.number().finite().positive(),
  })
  .strict();
export type WorkOrderPackagingLabelInput = z.infer<typeof WorkOrderPackagingLabelInput>;

// ── ArticleIngredient ────────────────────────────────────────────────────────

/**
 * One line of an article's DECLARED composition (article_ingredients): the
 * label-facing sub-ingredients of a compound raw material (Farina de Força →
 * Blat, Gluten, Sègol, E-345…), expanded into the FT ingredients text.
 * `percentage` is the optional QUID declaration. Ordering via sort_order.
 */
export const ArticleIngredientInput = z
  .object({
    article_id: uuid,
    ingredient_id: uuid,
    sort_order: z.number().int().min(0).default(0),
    percentage: z.number().finite().min(0).max(100).optional(),
  })
  .strict();
export type ArticleIngredientInput = z.infer<typeof ArticleIngredientInput>;

// ── ArticleFoodSafetyNote ────────────────────────────────────────────────────

/**
 * One free-text block of an article's "Seguretat alimentària" section
 * (article_food_safety_notes). Replaces the fixed parameters the section used to
 * hold; the obrador writes whatever it needs and adds as many blocks as it wants.
 * Ordering via sort_order = the FT render order. A child table rather than a column
 * on articles so the admin/quality write gate is preserved (articles UPDATE excludes
 * quality) — see the 20260721140000 migration header.
 */
export const ArticleFoodSafetyNoteInput = z
  .object({
    article_id: uuid,
    title: nonEmptyString.max(200).optional(),
    body: nonEmptyString.max(4000),
    sort_order: z.number().int().min(0).default(0),
  })
  .strict();
export type ArticleFoodSafetyNoteInput = z.infer<typeof ArticleFoodSafetyNoteInput>;

// ── ArticleComplianceItem ────────────────────────────────────────────────────

/** Attach a compliance / legislation claim ("No conté OMG") to an article. */
export const ArticleComplianceItemInput = z
  .object({
    article_id: uuid,
    compliance_item_id: uuid,
  })
  .strict();
export type ArticleComplianceItemInput = z.infer<typeof ArticleComplianceItemInput>;

// ── SupplierArticle ──────────────────────────────────────────────────────────

/**
 * Article ↔ supplier link (supplier_articles): n suppliers per article, at most
 * one active `primary` (DB partial unique index). Carries the supplier's own
 * code + description for the article, the last agreed price AT THE ARTICLE
 * BASE UNIT, and the supplier's tech-sheet document (storage url + key).
 * tenant_id is NEVER accepted from the client.
 */
export const SupplierArticleInput = z
  .object({
    article_id: uuid,
    supplier_id: uuid,
    link_role: z.enum(SUPPLIER_LINK_ROLES).default('alternative'),
    supplier_ref: nonEmptyString.nullable().optional(),
    supplier_description: nonEmptyString.nullable().optional(),
    last_price: money.nullable().optional(),
    // ENTRADA d'un sol ús (OBR-131): d'on ve el canvi de preu d'ESTA escriptura. El trigger
    // de la base de dades la llegeix i la buida; sense declarar, el canvi és «manual».
    // `price_change_ref` és l'id de la comanda (purchase_order) o de la importació (import).
    price_change_source: z.enum(DECLARABLE_PRICE_CHANGE_SOURCES).optional(),
    price_change_ref: uuid.optional(),
    // Default purchase unit for THIS supplier (decision Q2 = per proveïdor):
    // references the packaging_units master (Caixa/Sac/…); null = base unit.
    purchase_unit_id: uuid.nullable().optional(),
    tech_sheet_url: nonEmptyString.nullable().optional(),
    tech_sheet_key: nonEmptyString.nullable().optional(),
  })
  .strict();
export type SupplierArticleInput = z.infer<typeof SupplierArticleInput>;

// ── Supplier ─────────────────────────────────────────────────────────────────

export const Supplier = z
  .object({
    id: uuid,
    tenant_id: uuid,
    /** Únic per obrador entre els actius; el genera el servidor si no s'envia. */
    code: z.string().trim().min(1).nullable(),
    name: nonEmptyString,
    /** Nom comercial, quan difereix de la raó social (`name`). */
    commercial_name: z.string().trim().min(1).nullable(),
    tax_id: z.string().trim().min(1).nullable(),
    contact_json: jsonObject,
    address: z.string().trim().min(1).nullable(),
    // RETIRADA (OBR-119): la columna es manté a la BD però cap pantalla la llegeix ni
    // l'escriu; el contingut va passar a `notes`. Es continua acceptant aquí perquè la
    // fila que torna la BD l'inclou i `.strict()` la rebutjaria.
    commercial_terms: z.string().trim().min(1).nullable(),
    notes: z.string().trim().min(1).nullable(),
    // Com rep les comandes (OBR-79) — veure SupplierInput.
    order_channel: z.enum(PURCHASE_ORDER_CHANNELS),
    order_emails: z.array(z.string()),
    order_phone: z.string().trim().min(1).nullable(),
    // Correu de qualitat (OBR-125): on van les sol·licituds i renovacions de fitxes tècniques.
    quality_email: z.string().trim().min(1).nullable(),
    order_language: z.enum(APP_LANGUAGES).nullable(),
    order_subject: z.string().trim().min(1).nullable(),
    order_message: z.string().trim().min(1).nullable(),
    order_attach_pdf: z.boolean(),
    order_auto_send: z.boolean(),
    // Homologació (OBR-166): NOMÉS de lectura. L'estat, la data i l'autor els escriu l'RPC
    // `set_supplier_homologation`; `SupplierInput` no els accepta (el disparador de la base de dades els rebutja).
    homologation_status: z.enum(SUPPLIER_HOMOLOGATION_STATUSES),
    homologated_at: timestamptz.nullable(),
    homologated_by: uuid.nullable(),
    homologation_notes: z.string().trim().min(1).nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Supplier = z.infer<typeof Supplier>;

/**
 * Structured supplier contact (persisted in `suppliers.contact_json`). Every field
 * is optional — the obrador fills what it has. Kept to a known shape (not free-form)
 * so the article / reception flows can read it later without guessing keys.
 */
export const SupplierContact = z
  .object({
    contact_name: nonEmptyString.optional(),
    phone: nonEmptyString.optional(),
    email: z.string().trim().email().optional(),
  })
  .strict();
export type SupplierContact = z.infer<typeof SupplierContact>;

/**
 * Supplier create/update payload (settings · proveïdors). The SAME schema validates
 * create and update; `tenant_id` is NEVER accepted from the client (RLS + the DB
 * DEFAULT inject it) and `.strict()` rejects a forged one. The optionals are
 * nullable so an edit that clears them persists the clear; `contact_json` defaults
 * to `{}` server-side when omitted.
 *
 * `code` és opcional a propòsit: el mestre el pot rebre (fitxa manual, importació
 * d'una llista que ja el porta) però mai és obligatori — quan falta, el trigger
 * `assign_supplier_code` el genera amb la màscara de l'obrador. Enviar-lo buit
 * NO és el mateix que no enviar-lo: `nonEmptyString` rebutja la cadena buida
 * perquè un codi en blanc no arribi a la base de dades disfressat de valor.
 */
export const SupplierInput = z
  .object({
    code: nonEmptyString.nullable().optional(),
    name: nonEmptyString,
    commercial_name: nonEmptyString.nullable().optional(),
    tax_id: nonEmptyString.nullable().optional(),
    contact_json: SupplierContact.optional(),
    address: nonEmptyString.nullable().optional(),
    // «Condicions comercials» ja no és un camp (OBR-119): la informació no estructurada
    // va a `notes`. Cap `commercial_terms` aquí: `.strict()` el rebutja.
    notes: nonEmptyString.nullable().optional(),
    /**
     * Dies habituals d'entrega en numeració ISO-8601 (1 = dilluns … 7 = diumenge),
     * la mateixa d'`EXTRACT(ISODOW)` i la que valida el CHECK de la columna. Llista
     * buida = cap de configurat. Veure domain/supplierDelivery.ts.
     */
    delivery_weekdays: z.array(z.number().int().min(1).max(7)).optional(),
    /**
     * Com rep les comandes (OBR-79). `email`: Obrava les hi envia; `whatsapp`: les rep
     * per WhatsApp al número de comandes (l'enviament des d'Obrava és OBR-93; mentrestant
     * es tracta com `manual`); `manual`: les rep per altres mitjans i Obrava només en
     * deixa constància.
     */
    order_channel: z.enum(PURCHASE_ORDER_CHANNELS).optional(),
    /**
     * On s'envien per correu. Com a molt 10 (CHECK). Obligatori amb canal `email`
     * (ho fan complir la fitxa i l'importador, vegeu domain/supplierOrdering.ts);
     * els proveïdors antics que no en tenen continuen enviant-se al correu de contacte.
     */
    order_emails: z.array(z.string().trim().email()).max(10).optional(),
    /**
     * Telèfon/WhatsApp de comandes (OBR-119), independent del telèfon general de
     * `contact_json`. Obligatori amb canal `whatsapp`.
     */
    order_phone: nonEmptyString.nullable().optional(),
    /**
     * Correu de QUALITAT (OBR-125): on van les sol·licituds i renovacions de fitxes tècniques.
     * Independent del correu general (`contact_json.email`) i del de comandes (`order_emails`).
     */
    quality_email: z.string().trim().email().nullable().optional(),
    /** Idioma del correu i del PDF; null = el de qui envia. */
    order_language: z.enum(APP_LANGUAGES).nullable().optional(),
    /** Assumpte i missatge propis; admeten {{comanda}}, {{obrador}}, {{entrega}}, {{proveidor}}. */
    order_subject: nonEmptyString.nullable().optional(),
    order_message: nonEmptyString.nullable().optional(),
    order_attach_pdf: z.boolean().optional(),
    /** Confirmar una comanda l'envia sola. */
    order_auto_send: z.boolean().optional(),
  })
  .strict();
export type SupplierInput = z.infer<typeof SupplierInput>;

/**
 * Document d'homologació d'un proveïdor (`supplier_documents`, OBR-166): el registre sanitari i els
 * certificats opcionals (IFS, BRC, ecològic…). Mateix esquema per a l'alta i per a la modificació; el
 * proveïdor (`supplier_id`) s'hi posa a l'alta i no es canvia mai, i `tenant_id` no s'accepta mai del client
 * (l'RLS i el DEFAULT de la columna el posen; `.strict()` en rebutja un de falsificat). Cap data d'emissió
 * posterior a la de caducitat.
 */
export const SupplierDocumentInput = z
  .object({
    doc_type: z.enum(SUPPLIER_DOCUMENT_TYPES),
    /** «RGSEAA», «IFS», «BRC», «Ecològic»… */
    name: nonEmptyString.max(120),
    doc_number: nonEmptyString.max(120).nullable().optional(),
    issued_on: dateString.nullable().optional(),
    expires_on: dateString.nullable().optional(),
    /** El fitxer, al bucket privat `quality-documents` (clau `<tenant>/suppliers/<proveïdor>/<nom>-<uuid>.<ext>`). */
    file_url: nonEmptyString.nullable().optional(),
    file_key: nonEmptyString.nullable().optional(),
    notes: nonEmptyString.max(2000).nullable().optional(),
  })
  .strict()
  .refine((doc) => documentDatesError(doc.issued_on, doc.expires_on) === null, {
    message: 'expires_before_issued',
    path: ['expires_on'],
  });
export type SupplierDocumentInput = z.infer<typeof SupplierDocumentInput>;

// ── SupplierLot ───────────────────────────────────────────────────────────────

/**
 * Supplier lot — ALWAYS created at MP reception (start of backward traceability).
 * `internal_lot_number` is auto-generated server-side (never manual), so the read shape
 * includes it but the input schema does NOT.
 */
export const SupplierLot = z
  .object({
    id: uuid,
    tenant_id: uuid,
    article_id: uuid,
    reception_id: uuid,
    supplier_id: uuid.nullable(),
    supplier_lot_code: z.string().trim().min(1).nullable(),
    internal_lot_number: nonEmptyString,
    expiry_date: dateString.nullable(),
    received_quantity: nonNegativeQuantity,
    unit_id: uuid,
    /** Where the lot lives now (Zona / Secció / Ubicació). */
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    status: z.enum(SUPPLIER_LOT_STATUSES),
    handling_unit_id: uuid.nullable(),
    // Compres i Recepció: link to the ordered line (Flux A), what the operator
    // counted in the purchase (packaging) unit, and the reception QC result.
    purchase_order_line_id: uuid.nullable(),
    purchase_unit_id: uuid.nullable(),
    purchase_quantity: nonNegativeQuantity.nullable(),
    reception_control_result: z.enum(RECEPTION_CONTROL_RESULTS).nullable(),
    reception_control_note: z.string().trim().min(1).nullable(),
    ...timestampFields,
    ...syncFields,
  })
  .strict();
export type SupplierLot = z.infer<typeof SupplierLot>;

// ── Reception ──────────────────────────────────────────────────────────────────

export const Reception = z
  .object({
    id: uuid,
    tenant_id: uuid,
    supplier_id: uuid.nullable(),
    reception_date: dateString,
    status: z.enum(RECEPTION_STATUSES),
    // Nº albarà del proveïdor: documental-traceability umbrella, mandatory once
    // confirmed (DB CHECK). Own sequential albarà de recepció number (server).
    supplier_delivery_note_number: z.string().trim().min(1).nullable(),
    reception_number: z.string().trim().min(1).nullable(),
    document_url: z.string().trim().min(1).nullable(),
    ocr_payload_json: jsonObject.nullable(),
    purchase_order_id: uuid.nullable(),
    // Escenari B (validació manual) + Escenari A (ERP) hooks.
    invoiced: z.boolean(),
    supplier_invoice_number: z.string().trim().min(1).nullable(),
    erp_export_status: z.string().trim().min(1).nullable(),
    erp_exported_at: timestamptz.nullable(),
    notes: z.string().trim().min(1).nullable(),
    confirmed_at: timestamptz.nullable(),
    created_by: uuid.nullable(),
    ...timestampFields,
    ...softDeleteFields,
    ...syncFields,
  })
  .strict();
export type Reception = z.infer<typeof Reception>;

/**
 * A single received line within a reception. Per-line supplier lot + expiry are
 * OPTIONAL (two-level traceability: the documental fallback is the mandatory
 * reception delivery-note number). `purchase_order_line_id` links the line to the
 * ordered line (Flux A); `purchase_unit_id` + `purchase_quantity` capture what the
 * operator counted (e.g. 2 Sacs) while `received_quantity` + `unit_id` are the
 * base-unit truth used by stock.
 */
export const ReceptionLineInput = z
  .object({
    article_id: uuid,
    supplier_lot_code: z.string().trim().min(1).optional(),
    expiry_date: dateString.optional(),
    received_quantity: nonNegativeQuantity,
    unit_id: uuid,
    zone_id: uuid.optional(),
    purchase_order_line_id: uuid.optional(),
    purchase_unit_id: uuid.optional(),
    purchase_quantity: nonNegativeQuantity.optional(),
    reception_control_result: z.enum(RECEPTION_CONTROL_RESULTS).optional(),
    reception_control_note: nonEmptyString.optional(),
  })
  .strict();
export type ReceptionLineInput = z.infer<typeof ReceptionLineInput>;

/**
 * Manual reception create payload. Server ALWAYS creates a supplier_lot per line (with an
 * auto internal_lot_number) + a reception stock movement. `internal_lot_number` and
 * `reception_number` are never supplied by the client. The supplier delivery-note number
 * is MANDATORY (the documental-traceability umbrella, invariant).
 */
export const ReceptionCreateInput = z
  .object({
    supplier_id: uuid.optional(),
    purchase_order_id: uuid.optional(),
    supplier_delivery_note_number: nonEmptyString,
    reception_date: dateString.optional(),
    notes: z.string().trim().min(1).optional(),
    lines: z.array(ReceptionLineInput).min(1),
  })
  .strict();
export type ReceptionCreateInput = z.infer<typeof ReceptionCreateInput>;

/**
 * The three independent flags that make up an article's NATURALESA: whether it is
 * bought, made in-house and/or sold. They are not mutually exclusive — a semi-finished
 * item is routinely both manufactured and sold.
 */
export const ARTICLE_NATURE_FLAGS = ['is_purchased', 'is_manufactured', 'is_sold'] as const;
export type ArticleNatureFlag = (typeof ARTICLE_NATURE_FLAGS)[number];

/**
 * One bulk edit applied to a selection of articles from the catalog grid.
 *
 * A DISCRIMINATED UNION on purpose: each action touches exactly one CONCERN, so a
 * mis-click can never rewrite a field the user was not looking at.
 *
 * The `nature` variant carries the three flags as OPTIONAL keys, because an article
 * normally holds more than one at once — a producte acabat is both manufactured and
 * sold — and setting them one at a time meant two passes over the same selection. An
 * ABSENT key means "leave it exactly as it is": the same rule the importer applies to a
 * blank cell, and the reason marking fifty items as sold still cannot silently clear
 * whether they are also purchased. At least one flag must be present — an edit that
 * writes nothing is a bug, not a no-op.
 */
export const ArticleBulkEditInput = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('type'), article_type: z.enum(ARTICLE_TYPES) }).strict(),
    z.object({ kind: z.literal('category'), category_id: uuid.nullable() }).strict(),
    // L'impost del mestre (taxes). Nullable: «sense impost» és una resposta, i
    // és la que cal per desfer una assignació equivocada de cent files.
    z.object({ kind: z.literal('tax'), tax_id: uuid.nullable() }).strict(),
    z
      .object({
        kind: z.literal('nature'),
        // Mirrors ARTICLE_NATURE_FLAGS above; spelled out so the inferred type keeps
        // one optional boolean per flag instead of a widened record.
        flags: z
          .object({
            is_purchased: z.boolean().optional(),
            is_manufactured: z.boolean().optional(),
            is_sold: z.boolean().optional(),
          })
          .strict(),
      })
      .strict(),
  ])
  .refine((edit) => edit.kind !== 'nature' || Object.keys(edit.flags).length > 0, {
    message: 'a nature edit must carry at least one flag',
    path: ['flags'],
  });
export type ArticleBulkEditInput = z.infer<typeof ArticleBulkEditInput>;

/**
 * The column patch a validated bulk edit stands for: the single place that decides what
 * an edit actually WRITES, so the UI and the data layer cannot drift apart.
 *
 * Turning «es fabrica» OFF also pins food_safety_mode to 'manual'. That is not a
 * courtesy — the CHECK articles_food_safety_mode_chk forbids a derived article that is
 * not manufactured, the food-safety migration backfilled EVERY manufactured article to
 * 'derived', and the bulk write is one atomic PATCH: without this, a single derived
 * article in the selection rejects the whole batch. The single-article form normalises
 * identically (formToUpdatePatch).
 *
 * The reverse is deliberately NOT normalised: turning «es fabrica» ON leaves the mode
 * alone, because ENTERING derived mode deletes the article's manual allergen marks and
 * its declared composition (clear_manual_food_safety_on_manufacture). Regulatory data
 * is never wiped as the side effect of a flag in a grid.
 */
export function articleBulkEditPatch(
  edit: ArticleBulkEditInput,
): Record<string, unknown> {
  switch (edit.kind) {
    case 'type':
      // La unitat de recepta només és d'una matèria primera (CHECK articles_recipe_unit_chk,
      // OBR-102): passar a qualsevol altra tipologia la neteja, o la base de dades rebutjaria
      // el canvi sencer.
      return edit.article_type === 'raw_material'
        ? { article_type: edit.article_type }
        : { article_type: edit.article_type, recipe_unit_id: null, recipe_unit_factor: null };
    case 'category':
      return { category_id: edit.category_id };
    case 'tax':
      // `vat_rate` no s'hi escriu: el deriva la base de dades del tipus de
      // l'impost (articles_sync_vat_rate), aquí i a la fitxa igual.
      return { tax_id: edit.tax_id };
    case 'nature': {
      const patch: Record<string, unknown> = { ...edit.flags };
      if (edit.flags.is_manufactured === false) patch.food_safety_mode = 'manual';
      return patch;
    }
  }
}
